import { Controller } from '@hotwired/stimulus'

export default class DirectCardPayinController extends Controller {
  static targets = [
    'registeredCardsList',
    'selectionErrorContainer',
    'selectionErrorMessage',
    'selectionCaret',
    'selectionLoader',
    'cardNumber',
    'expirationDate',
    'cvv',
    'cardName',
    'saveCard',
    'submitBtn',
    'formLoader',
    'formSubmitText',
    'formErrorContainer',
    'formErrorMessage',
    'cardProviderIcon',
  ];

  static values = {
    createUrl: String,
    payUrl: String,
    amount: Number,
    errorGeneric: String,
    errorCardCreation: String,
    errorCardValidation: String,
    errorPayment: String,
    visaIcon: String,
    mastercardIcon: String,
    amexIcon: String,
    genericIcon: String,
  };

  connect() {
    this.processing = false;
  }

  reset() {
    this.processing = false;
    this.cardNumberTarget.value = '';
    this.expirationDateTarget.value = '';
    this.cvvTarget.value = '';
    this.cardNameTarget.value = '';
    this.saveCardTarget.checked = false;
    this.hideSelectionError();
    this.hideFormError();
    this.setFormProcessing(false);
    this.resetSelectionProcessing();
    this.resetCardProviderIcon();
    this.updateSubmitState();
  }

  hasRegisteredCards() {
    return this.registeredCardsListTarget.children.length > 0;
  }

  // --- Card Selection ---

  async selectRegisteredCard(event) {
    if (this.processing) return;

    const btn = event.currentTarget;
    if (btn.dataset.expired === 'true') return;

    this.processing = true;
    this.activeCardBtn = btn;
    const cardId = btn.dataset.cardId;

    this.hideSelectionError();
    this.setSelectionProcessing(true, btn);

    try {
      const browserInfo = this.collectBrowserInfo();
      const response = await fetch(this.payUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          registered_card_id: cardId,
          amount: this.amountValue,
          browser_info: browserInfo,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || this.errorGenericValue);
      }

      this.handlePayInResponse(data, 'selection');
    } catch (error) {
      this.showSelectionError(error.message);
      this.setSelectionProcessing(false, btn);
    }
  }

  // --- New Card Form ---

  onCardNumberInput(event) {
    let value = event.target.value.replace(/\D/g, '');
    let formatted = value.replace(/(.{4})/g, '$1 ').trim();
    event.target.value = formatted;

    this.detectCardProvider(value);
    this.updateSubmitState();
  }

  onExpirationDateInput(event) {
    const isBackspace = event.inputType === 'deleteContentBackward';

    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);
    if (value.length > 2 || (value.length === 2 && !isBackspace)) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    event.target.value = value;
    this.updateSubmitState();
  }

  onFormInput() {
    this.updateSubmitState();
  }

  updateSubmitState() {
    const cardNumber = this.cardNumberTarget.value.replace(/\s/g, '');
    const expiration = this.expirationDateTarget.value.replace(/\D/g, '');
    const cvv = this.cvvTarget.value;
    const cardName = this.cardNameTarget.value.trim();

    const isValid = cardNumber.length > 0 &&
                    expiration.length > 0 &&
                    cvv.length > 0 &&
                    cardName.length > 0;

    this.submitBtnTarget.disabled = !isValid;
  }

  detectCardProvider(cardNumber) {
    if (!cardNumber || cardNumber.length < 1) {
      this.resetCardProviderIcon();
      return;
    }

    const num = parseInt(cardNumber.substring(0, 4), 10);
    const twoDigit = parseInt(cardNumber.substring(0, 2), 10);
    let iconUrl = null;

    if (cardNumber.startsWith('4')) {
      iconUrl = this.visaIconValue;
    } else if ((twoDigit >= 51 && twoDigit <= 55) || (num >= 2221 && num <= 2720)) {
      iconUrl = this.mastercardIconValue;
    } else if (twoDigit === 34 || twoDigit === 37) {
      iconUrl = this.amexIconValue;
    } else {
      iconUrl = this.genericIconValue;
    }

    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = '';
    img.width = 16;
    img.height = 16;
    this.cardProviderIconTarget.replaceChildren(img);
  }

  resetCardProviderIcon() {
    const icon = document.createElement('i');
    icon.className = 'ph ph-credit-card fs-6';
    icon.setAttribute('aria-hidden', 'true');
    this.cardProviderIconTarget.replaceChildren(icon);
  }

  async submit(event) {
    if (event) event.preventDefault();
    if (this.processing) return;

    this.hideFormError();
    this.setFormProcessing(true);

    try {
      const cardRegistration = await this.createCardRegistration();
      const registrationData = await this.tokenizeCard(cardRegistration);
      const result = await this.updateAndPayIn(cardRegistration.card_registration_id, registrationData);

      this.handlePayInResponse(result, 'form');
    } catch (error) {
      this.showFormError(error.message || this.errorGenericValue);
      this.setFormProcessing(false);
    }
  }

  // Step 1: Create a CardRegistration on MangoPay
  async createCardRegistration() {
    const response = await fetch(this.createUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        amount: this.amountValue,
        save_card: this.saveCardTarget.checked,
        card_holder_name: this.cardNameTarget.value.trim(),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || this.errorCardCreationValue);
    }

    return data;
  }

  // Step 2: Browser posts card data to CardRegistrationURL (client-side tokenization)
  async tokenizeCard(cardRegistration) {
    const cardNumber = this.cardNumberTarget.value.replace(/\s/g, '');
    const expirationDate = this.expirationDateTarget.value.replace('/', '');
    const cvv = this.cvvTarget.value;

    const formData = new URLSearchParams();
    formData.append('data', cardRegistration.preregistration_data);
    formData.append('accessKeyRef', cardRegistration.access_key);
    formData.append('cardNumber', cardNumber);
    formData.append('cardExpirationDate', expirationDate);
    formData.append('cardCvx', cvv);

    const response = await fetch(cardRegistration.card_registration_url, {
      method: 'POST',
      body: formData,
    });

    const registrationData = await response.text();

    if (!response.ok || registrationData.startsWith('errorCode=')) {
      throw new Error(this.errorCardValidationValue);
    }

    return registrationData;
  }

  // Step 3: Create Direct PayIn (update card registration + pay)
  async updateAndPayIn(cardRegistrationId, registrationData) {
    const browserInfo = this.collectBrowserInfo();

    const response = await fetch(this.payUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        registration_id: cardRegistrationId,
        registration_data: registrationData,
        save_card: this.saveCardTarget.checked,
        card_holder_name: this.cardNameTarget.value.trim(),
        amount: this.amountValue,
        browser_info: browserInfo,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || this.errorPaymentValue);
    }

    return data;
  }

  // --- Shared helpers ---

  handlePayInResponse(result, context) {
    // Step 4: 3DS redirect (browser-side, user authenticates with their bank)
    if (result.secure_mode) {
      window.location.href = result.redirect_url;
    } else if (result.success) {
      window.location.href = result.redirect_url;
    } else if (result.error) {
      if (context === 'selection') {
        this.showSelectionError(result.error);
        this.setSelectionProcessing(false, this.activeCardBtn);
      } else {
        this.showFormError(result.error);
        this.setFormProcessing(false);
      }
    }
  }

  collectBrowserInfo() {
    return {
      java_enabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
      javascript_enabled: true,
      language: navigator.language || navigator.userLanguage,
      color_depth: screen.colorDepth,
      screen_height: screen.height,
      screen_width: screen.width,
      timezone_offset: new Date().getTimezoneOffset(),
    };
  }

  // --- Selection step UI ---

  showSelectionError(message) {
    this.selectionErrorMessageTarget.textContent = message;
    this.selectionErrorContainerTarget.classList.remove('d-none');
  }

  hideSelectionError() {
    this.selectionErrorContainerTarget.classList.add('d-none');
    this.selectionErrorMessageTarget.textContent = '';
  }

  setSelectionProcessing(isProcessing, btn) {
    this.processing = isProcessing;
    const caret = this.selectionCaretTargets.find(t => btn.contains(t));
    const loader = this.selectionLoaderTargets.find(t => btn.contains(t));

    if (isProcessing) {
      caret.classList.add('d-none');
      loader.classList.remove('d-none');
    } else {
      caret.classList.remove('d-none');
      loader.classList.add('d-none');
    }
  }

  resetSelectionProcessing() {
    this.processing = false;
    this.selectionCaretTargets.forEach(t => t.classList.remove('d-none'));
    this.selectionLoaderTargets.forEach(t => t.classList.add('d-none'));
  }

  // --- Form step UI ---

  showFormError(message) {
    this.formErrorMessageTarget.textContent = message;
    this.formErrorContainerTarget.classList.remove('d-none');
  }

  hideFormError() {
    this.formErrorContainerTarget.classList.add('d-none');
    this.formErrorMessageTarget.textContent = '';
  }

  setFormProcessing(isProcessing) {
    this.processing = isProcessing;
    if (isProcessing) {
      this.formLoaderTarget.classList.remove('d-none');
      this.formSubmitTextTarget.classList.add('d-none');
    } else {
      this.formLoaderTarget.classList.add('d-none');
      this.formSubmitTextTarget.classList.remove('d-none');
    }
  }
}
